import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Clock, Crown, X } from "lucide-react";

const todayKey = () => `omni_trial_notice_${new Date().toISOString().slice(0, 10)}`;

export function FreeTrialNotice() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!token || user?.tier !== "free") return;
    axios.get(`${API}/auth/free-trial-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setStatus(data);
        if (data.show_warning) {
          const key = todayKey();
          const shownToday = parseInt(localStorage.getItem(key) || "0", 10);
          if (shownToday < 2) {
            localStorage.setItem(key, String(shownToday + 1));
            setShowWarning(true);
          }
        }
      })
      .catch(() => {});
  }, [token, user?.tier]);

  if (!status?.applicable) return null;

  if (status.expired) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div data-testid="free-trial-expired-modal" className="max-w-sm w-full bg-card border border-border/50 rounded-3xl p-6 shadow-2xl text-center">
          <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-6 h-6 text-destructive" />
          </div>
          <h3 className="text-lg font-heading font-semibold mb-2">Your Free plan has ended</h3>
          <p className="text-sm text-muted-foreground mb-5">
            Your one-time 30-day Free tier window is over. Free tier can't be renewed on this account —
            upgrade to a paid plan to keep chatting with Omni.
          </p>
          <button
            data-testid="free-trial-upgrade-btn"
            onClick={() => navigate("/pricing")}
            className="w-full py-3 rounded-2xl bg-primary text-primary-foreground text-sm font-medium shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <Crown className="w-4 h-4" /> See Upgrade Plans
          </button>
        </div>
      </div>
    );
  }

  if (showWarning) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div data-testid="free-trial-warning-modal" className="max-w-sm w-full bg-card border border-primary/20 rounded-3xl p-6 shadow-2xl text-center relative">
          <button
            data-testid="free-trial-dismiss-btn"
            onClick={() => setShowWarning(false)}
            className="absolute top-4 right-4 w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-lg font-heading font-semibold mb-2">
            {status.days_remaining} day{status.days_remaining === 1 ? "" : "s"} left on Free
          </h3>
          <p className="text-sm text-muted-foreground mb-5">
            Your Free plan is a one-time 30-day window. Upgrade now to keep uninterrupted access —
            Free tier can't be renewed once it ends.
          </p>
          <button
            data-testid="free-trial-upgrade-btn"
            onClick={() => navigate("/pricing")}
            className="w-full py-3 rounded-2xl bg-primary text-primary-foreground text-sm font-medium shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <Crown className="w-4 h-4" /> See Upgrade Plans
          </button>
        </div>
      </div>
    );
  }

  return null;
}
